// NEW_FILE_JUSTIFIED: Searchable employee select for policy form — follows CountryCombobox pattern, no existing equivalent
import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { IEmployeeComboboxProps } from '@/@types/insurance';

export function EmployeeCombobox({ employees, value, onChange, disabled }: IEmployeeComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = employees.find(e => e._id === value);
  const regularEmployees = employees.filter(e => !e.isTeamMember);
  const teamMembers = employees.filter(e => e.isTeamMember);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls="employee-combobox-list"
          disabled={disabled}
          className={cn(
            'w-full !h-10 justify-between font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          {selected?.name || 'Select employee'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search employee..." />
          <CommandList id="employee-combobox-list">
            <CommandEmpty>No employee found.</CommandEmpty>
            {teamMembers.length > 0 && (
              <CommandGroup heading="Team Members">
                {teamMembers.map(emp => (
                  <CommandItem
                    key={emp._id}
                    value={emp.name}
                    className="data-[selected=true]:text-white"
                    onSelect={() => {
                      onChange(emp._id === value ? '' : emp._id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === emp._id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="flex items-center gap-2">
                      {emp.name}
                      {emp.roles?.map(role => (
                        <Badge
                          key={role}
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 capitalize"
                        >
                          {role}
                        </Badge>
                      ))}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {regularEmployees.length > 0 && (
              <CommandGroup heading="Employees">
                {regularEmployees.map(emp => (
                  <CommandItem
                    key={emp._id}
                    value={emp.name}
                    className="data-[selected=true]:text-white"
                    onSelect={() => {
                      onChange(emp._id === value ? '' : emp._id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === emp._id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {emp.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
